import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  setDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  Box,
  AppBar,
  Toolbar,
  Avatar,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { alpha } from "@mui/material/styles";
import { getSession } from "../session";
import Atras from "./atras";
import ChatPolicyDialog from "./ChatPolicyDialog";
import { detectContactInfoAttempt, applyModerationStrike } from "./moderation";

const getConversationId = (uidA, uidB) => [uidA, uidB].sort().join("_");

const DirectChat = () => {
  const { uid: otherUid } = useParams();
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [moderationAlert, setModerationAlert] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const init = useCallback(async () => {
    const user = await getSession();
    if (!user) {
      navigate("/login");
      return;
    }

    const userDocSnapshot = await getDoc(doc(db, "users", user.uid));
    const userData = userDocSnapshot.exists() ? userDocSnapshot.data() : {};
    setAuthUser({
      uid: user.uid,
      displayName: userData.displayName || user.email,
      profileImageUrl: userData.profileImageUrl || null,
    });

    const otherDocSnapshot = await getDoc(doc(db, "users", otherUid));
    if (otherDocSnapshot.exists()) {
      setOtherUser(otherDocSnapshot.data());
    }

    const conversationId = getConversationId(user.uid, otherUid);
    await setDoc(
      doc(db, "directMessages", conversationId),
      { participants: [user.uid, otherUid], updatedAt: serverTimestamp() },
      { merge: true }
    );

    const messagesRef = collection(db, "directMessages", conversationId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "asc"), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      scrollToBottom();
    });

    setLoading(false);
    return unsubscribe;
  }, [otherUid, navigate]);

  useEffect(() => {
    let unsubscribe;
    init().then((unsub) => {
      unsubscribe = unsub;
    });
    return () => unsubscribe && unsubscribe();
  }, [init]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !authUser) return;

    const conversationId = getConversationId(authUser.uid, otherUid);
    const messagesRef = collection(db, "directMessages", conversationId, "messages");

    const { flagged, reasons } = detectContactInfoAttempt(newMessage);
    if (flagged) {
      try {
        const { accountStatus } = await applyModerationStrike({
          uid: authUser.uid,
          chatPath: `directMessages/${conversationId}/messages`,
          message: newMessage.trim(),
          reasons,
        });
        setModerationAlert(
          accountStatus === "pending_deletion"
            ? "Volviste a intentar compartir información de contacto. Tu cuenta fue marcada para revisión y puede ser eliminada."
            : "Tu mensaje parece incluir información de contacto. Está prohibido coordinar trabajo fuera de la plataforma. Tu cuenta quedó suspendida."
        );
        setTimeout(() => navigate("/home"), 3000);
      } catch (moderationError) {
        console.error("Error applying moderation strike:", moderationError);
        setModerationAlert(
          "Tu mensaje parece incluir información de contacto y no fue enviado. Compartir estos datos no está permitido."
        );
      }
      return;
    }

    await addDoc(messagesRef, {
      content: newMessage.trim(),
      timestamp: new Date(),
      senderId: authUser.uid,
      senderDisplayName: authUser.displayName,
      read: false,
    });

    await setDoc(
      doc(db, "directMessages", conversationId),
      {
        lastMessage: newMessage.trim(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setNewMessage("");
    scrollToBottom();
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = timestamp?.toDate ? timestamp.toDate() : timestamp;
    if (!date) return "";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "background.default", height: "100vh", display: "flex", flexDirection: "column" }}>
      <ChatPolicyDialog
        open={!policyAccepted}
        onAccept={() => setPolicyAccepted(true)}
        onCancel={() => navigate(-1)}
      />
      <AppBar position="static">
        <Toolbar sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Atras />
          <Avatar
            src={otherUser?.profileImageUrl}
            alt={otherUser?.displayName}
            sx={{ width: 36, height: 36 }}
          />
          <Typography variant="h6" color="text.primary">
            {otherUser?.displayName || "Usuario"}
          </Typography>
        </Toolbar>
      </AppBar>
      {moderationAlert && (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mx: "auto", mt: 1, maxWidth: 980, width: "100%" }}>
          {moderationAlert}
        </Alert>
      )}
      <Box sx={{ flexGrow: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        <Box sx={{ width: "100%", maxWidth: 980, mx: "auto", px: 2, py: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          {messages.length === 0 ? (
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                gap: 1,
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 48 }} />
              <Typography variant="body1">Todavía no hay mensajes</Typography>
              <Typography variant="body2">Escribí el primero para arrancar la conversación</Typography>
            </Box>
          ) : (
            messages.map((message) => {
              const isOwn = message.senderId === authUser.uid;
              return (
                <Box
                  key={message.id}
                  sx={{
                    display: "flex",
                    flexDirection: isOwn ? "row-reverse" : "row",
                    alignSelf: isOwn ? "flex-end" : "flex-start",
                    alignItems: "flex-end",
                    gap: 1,
                    maxWidth: "75%",
                    mb: 2,
                  }}
                >
                  <Avatar
                    src={isOwn ? authUser?.profileImageUrl : otherUser?.profileImageUrl}
                    sx={{ width: 28, height: 28, fontSize: 14 }}
                  >
                    {(isOwn ? authUser?.displayName : otherUser?.displayName)?.[0]?.toUpperCase() || "?"}
                  </Avatar>
                  <Box>
                    <Box
                      sx={{
                        bgcolor: isOwn
                          ? (theme) => alpha(theme.palette.primary.main, 0.18)
                          : "background.paper",
                        border: (theme) => (isOwn ? "none" : `1px solid ${theme.palette.divider}`),
                        p: 1.5,
                        borderRadius: "16px",
                        borderBottomRightRadius: isOwn ? "4px" : "16px",
                        borderBottomLeftRadius: isOwn ? "16px" : "4px",
                        boxShadow: isOwn ? "none" : 1,
                      }}
                    >
                      <Typography variant="body1" sx={{ color: "text.primary", whiteSpace: "pre-wrap" }}>
                        {message.content}
                      </Typography>
                    </Box>
                    {detectContactInfoAttempt(message.content).flagged && (
                      <Typography variant="caption" sx={{ color: "warning.main", display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                        <WarningAmberIcon sx={{ fontSize: 14 }} />
                        Este mensaje podría contener información de contacto
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", mt: 0.5, display: "block", textAlign: isOwn ? "right" : "left" }}
                    >
                      {formatTime(message.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>
      <Box sx={{ backgroundColor: "background.paper", borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ maxWidth: 980, mx: "auto", p: 2 }}>
          <TextField
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escribe tu mensaje..."
            variant="outlined"
            fullWidth
            multiline
            maxRows={4}
            disabled={!policyAccepted}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "24px" } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || !policyAccepted}
                    sx={{ minWidth: 0, borderRadius: "50%", width: 40, height: 40, p: 0 }}
                  >
                    <SendIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DirectChat;
